var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../icon/Icon.js';
/**
 * Generic breadcrumb shown in panel headers. The last item in `items` is
 * styled as the current location (bold, larger, primary color). Each item
 * receives its own click handler; the component also emits a bubbling
 * `breadcrumb-item-click` event `{ index, item }` for parents that prefer
 * a single listener.
 */
let Breadcrumb = class Breadcrumb extends LitElement {
    constructor() {
        super(...arguments);
        this.items = [];
        this.separator = '/';
    }
    _handleClick(index, item, event) {
        item.onClick?.(event);
        this.dispatchEvent(new CustomEvent('breadcrumb-item-click', {
            detail: { index, item },
            bubbles: true,
            composed: true,
        }));
    }
    _renderItem(item, index, isCurrent) {
        // The current item is read-only: it represents "you are here", so it
        // never reacts to clicks and never shows the trailing action icon.
        const isInteractive = !isCurrent && typeof item.onClick === 'function';
        const isIconOnly = !!item.icon && !item.label;
        const classes = [
            'item',
            isCurrent ? 'is-current' : '',
            isInteractive ? 'is-button' : '',
            isIconOnly ? 'is-icon-only' : '',
        ]
            .filter(Boolean)
            .join(' ');
        const content = html `
      ${item.icon
            ? html `<basic-icon name=${item.icon} size="15"></basic-icon>`
            : nothing}
      ${item.label ? html `<span class="label">${item.label}</span>` : nothing}
      ${!isCurrent && item.trailingIcon
            ? html `<span class="trailing"
            ><basic-icon name=${item.trailingIcon} size="11"></basic-icon
          ></span>`
            : nothing}
    `;
        if (!isInteractive) {
            return html `<span
        class=${classes}
        title=${item.title ?? nothing}
        aria-current=${isCurrent ? 'page' : nothing}
        >${content}</span
      >`;
        }
        const ariaLabel = item.ariaLabel ?? item.label ?? item.title;
        return html `<button
      type="button"
      class=${classes}
      aria-label=${ariaLabel ?? nothing}
      title=${item.title ?? nothing}
      @click=${(e) => this._handleClick(index, item, e)}
    >
      ${content}
    </button>`;
    }
    render() {
        const last = this.items.length - 1;
        return html `${this.items.map((item, i) => html `
        ${i > 0
            ? html `<span class="separator" aria-hidden="true">${this.separator}</span>`
            : nothing}
        ${this._renderItem(item, i, i === last)}
      `)}`;
    }
};
Breadcrumb.styles = css `
    :host {
      display: inline-flex;
      align-items: center;
      min-width: 0;
      flex: 1;
      gap: 4px;
      font-size: 12px;
      line-height: 1;
      color: var(--ggo-breadcrumb-color, #5a7b9c);
    }
    .item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: transparent;
      border: none;
      padding: 4px 8px;
      font: inherit;
      color: inherit;
      border-radius: 6px;
      cursor: default;
      flex-shrink: 0;
      transition: color 0.15s, background 0.15s;
    }
    .item.is-button {
      cursor: pointer;
    }
    .item.is-icon-only {
      width: 28px;
      height: 28px;
      padding: 0;
      justify-content: center;
    }
    .item.is-button:hover {
      background: var(--ggo-breadcrumb-hover-bg, rgba(255, 255, 255, 0.55));
      color: var(--ggo-breadcrumb-hover-color, #0078d4);
    }
    .item.is-current {
      flex: 1;
      min-width: 0;
      font-size: 15px;
      font-weight: 600;
      color: var(--ggo-breadcrumb-current-color, #004e8c);
      text-align: left;
    }
    .item.is-current .label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }
    .trailing {
      flex-shrink: 0;
      color: var(--ggo-breadcrumb-trailing-color, #5a7b9c);
      opacity: 0.85;
    }
    .item.is-button:hover .trailing {
      color: var(--ggo-breadcrumb-hover-color, #0078d4);
      opacity: 1;
    }
    .separator {
      color: var(--ggo-breadcrumb-separator-color, #b8c4d3);
      user-select: none;
      flex-shrink: 0;
    }
  `;
__decorate([
    property({ type: Array })
], Breadcrumb.prototype, "items", void 0);
__decorate([
    property({ type: String })
], Breadcrumb.prototype, "separator", void 0);
Breadcrumb = __decorate([
    customElement('ggo-breadcrumb')
], Breadcrumb);
export { Breadcrumb };
//# sourceMappingURL=Breadcrumb.js.map